import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, formatDateUK, apiError } from "@/lib/api";
import { Z_STATUS, Z_STATUS_CLS } from "@/lib/z";
import ReviewPanel from "@/components/ReviewPanel";
import AuditTimeline from "@/components/AuditTimeline";
import DocumentPreview from "@/components/DocumentPreview";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Pencil, Trash2, AlertTriangle } from "lucide-react";

const Row = ({ k, v, id, strong }) => (
  <div className="flex justify-between py-1.5 text-sm border-b border-navy/5 last:border-0">
    <span className="text-navy/55">{k}</span><span className={`font-mono text-navy ${strong ? "font-bold" : ""}`} data-testid={id}>{v}</span>
  </div>
);

function Recon({ z, onDone }) {
  const { user } = useAuth();
  const [reason, setReason] = useState("");
  const rec = z.reconciliation;
  if (!rec || (rec.matched && !z.recon_exception_reason)) return null;
  const manager = ["store_manager", "owner"].includes(user?.role);
  const authorise = async () => {
    try { await api.post(`/z-closings/${z.id}/reconciliation-exception`, { reason }); toast.success("Difference authorised"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2" data-testid="z-recon-panel">
      <p className="text-sm font-semibold text-amber-900 flex gap-2"><AlertTriangle className="w-4 h-4" /> Account sales check</p>
      <p className="text-sm text-amber-900">Z account sales {formatGBP(rec.z_account_sales)} · named customer credit sales {formatGBP(rec.named_customer_credit_sales)} · difference <span data-testid="z-recon-diff">{formatGBP(rec.difference)}</span></p>
      {z.recon_exception_reason ? <p className="text-xs text-amber-900" data-testid="z-recon-exception">Authorised by a manager: {z.recon_exception_reason}</p>
        : <p className="text-xs text-amber-900">Add the missing customer credit sales for this day, or a manager can authorise the difference.</p>}
      {manager && z.status === "submitted" && !z.recon_exception_reason && !rec.matched && (
        <div className="space-y-2">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why the difference is acceptable (at least 10 characters)" data-testid="z-recon-reason-input" />
          <button onClick={authorise} disabled={reason.trim().length < 10} className="w-full min-h-[44px] rounded-xl bg-navy text-white text-sm disabled:opacity-40" data-testid="z-recon-authorise-btn">Authorise difference</button>
        </div>
      )}
    </div>
  );
}

function OwnerActions({ z, navigate }) {
  const { user } = useAuth();
  if (z.submitted_by !== user?.id || !["draft", "rejected"].includes(z.status)) return null;
  const discard = async () => {
    if (!window.confirm("Discard this draft Z report?")) return;
    try { await api.delete(`/z-closings/${z.id}`); toast.success("Draft discarded"); navigate("/z-closings"); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex gap-3">
      <button onClick={() => navigate(`/edit-z/${z.id}`)} className="flex-1 gm-btn-gold flex items-center justify-center gap-2" data-testid="z-edit-btn"><Pencil className="w-4 h-4" /> {z.status === "rejected" ? "Correct and resubmit" : "Continue draft"}</button>
      {z.status === "draft" && <button onClick={discard} className="gm-btn-navy px-4 flex items-center gap-2" data-testid="z-discard-btn"><Trash2 className="w-4 h-4" /> Discard</button>}
    </div>
  );
}

export default function ViewZPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [z, setZ] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => { api.get(`/z-closings/${id}`).then((r) => setZ(r.data)).catch(() => setZ(false)); }, [id, reload]);
  if (z === false) return <p className="text-sm text-navy/50" data-testid="z-not-found">Z report not found.</p>;
  if (z === false) return <p className="text-sm text-navy/60" data-testid="z-not-found">This Z report was not found, or it belongs to a store you cannot see.</p>;
  if (!z) return <p className="text-sm text-navy/50">Loading…</p>;
  const returned = [...(z.history || [])].reverse().find((h) => h.action === "returned");
  return (
    <div className="space-y-5" data-testid="view-z-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/z-closings")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
        <div><h1 className="text-xl font-heading font-bold text-navy">Z report · {z.register_ref}</h1><p className="text-xs text-navy/50">{z.store_name} · {formatDateUK(z.trading_date)}</p></div>
        <span className={`ml-auto text-xs px-2 py-1 rounded-full ${Z_STATUS_CLS[z.status]}`} data-testid="z-detail-status">{Z_STATUS[z.status]}</span>
      </div>
      {z.status === "rejected" && returned && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="z-returned-reason">Returned by {returned.user_name}: {returned.reason}</div>}
      <OwnerActions z={z} navigate={navigate} />
      <div className="rounded-xl border border-navy/10 bg-white p-4">
        <Row k="Cash" v={formatGBP(z.cash_sales)} id="z-detail-cash" />
        <Row k={z.card_machines.length ? "Card (all machines)" : "Card"} v={formatGBP(z.card_sales)} id="z-detail-card" />
        {z.card_machines.map((m, i) => <Row key={i} k={`  · ${m.name}`} v={formatGBP(m.amount)} id={`z-detail-machine-${i}`} />)}
        <Row k="Account / credit" v={formatGBP(z.account_sales)} id="z-detail-account" />
        <Row k="Bank transfer" v={formatGBP(z.bank_transfer_sales)} id="z-detail-bank" />
        {z.other_tenders.map((t, i) => <Row key={i} k={t.name} v={formatGBP(t.amount)} id={`z-detail-other-${i}`} />)}
        <Row k="Cash refunds" v={formatGBP(z.cash_refunds)} id="z-detail-cash-refunds" />
        <Row k="Card refunds" v={formatGBP(z.card_refunds)} id="z-detail-card-refunds" />
        <Row k="Other refunds" v={formatGBP(z.other_refunds)} id="z-detail-other-refunds" />
        {z.refunds > 0 && <Row k="Tender figures" v={z.refund_layout === "net" ? "after refunds" : "before refunds"} id="z-detail-refund-layout" />}
        <Row k="Adjustments" v={formatGBP(z.adjustments)} id="z-detail-adjustments" />
        <Row k="Total sales" v={formatGBP(z.total_sales)} id="z-detail-total" strong />
        <p className="text-xs text-navy/45 pt-2">Entered by {z.submitted_by_name} · {z.source === "upload" ? "from the Z report photo" : "typed in"}</p>
      </div>
      <Recon z={z} onDone={() => setReload((n) => n + 1)} />
      {z.approval_blocked && <p className="text-xs text-amber-700" data-testid="z-approval-blocked">{z.approval_blocked.message}</p>}
      <ReviewPanel entity="z-closings" data={z} onDone={() => setReload((n) => n + 1)} />
      {(z.documents || []).map((d) => <DocumentPreview key={d.id} doc={d} />)}
      <AuditTimeline history={z.history} kind="z_closing" />
    </div>
  );
}
